import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { HeaderComponent } from '../header/header';
import { SettingsNavComponent } from '../settings-nav/settings-nav';
import { BrnService, BrnPlanResponse, BrnPlanRequest } from '../../services/brn.service';
import { Bot } from '../../interfaces/bot.interface';
import { SHOPIFY_CLIENT_ID, SHOPIFY_SCOPE, REDIRECT_URI, SLACK_CLIENT_ID, SLACK_SCOPE, SLACK_REDIRECT_URI, FACEBOOK_APP_ID, FACEBOOK_SCOPE, GOOGLE_ADS_REDIRECT_URI } from '../../constants';
import { FacebookService, InitParams } from 'ngx-facebook';
import { GoogleAdsCustomer } from '../../services/brn.service';

@Component({
  selector: 'app-store',
  standalone: true,
  imports: [CommonModule, FormsModule, SettingsNavComponent],
  templateUrl: './store.html',
  styleUrls: ['./store.scss']
})
export class StoreComponent implements OnInit, OnDestroy {
  private shopifySubject = new Subject<void>();
  private planSubject = new Subject<void>();
  private destroy$ = new Subject<void>();
  
  botId: string = '';
  botData: Bot | null = null;
  showShopifyPopup: boolean = false;
  shopifyUrl: string = '';
  isShopifyConnected: boolean = false;
  shopifyService: any = null;
  isSlackConnected: boolean = false;
  isToolCreated: boolean = false;
  toolData: any = null;
  loading: boolean = false;
  slackLoading: boolean = false;
  slackChannel: string = '';
  shopifyShop: string = '';
  shopifyApiToken: string = '';
  facebookInitialized: boolean = false;
  adAccounts: any[] = [];
  selectedAdAccount: any = null;
  showAdAccountDropdown: boolean = false;
  dropdownOpen: boolean = false;
  isFacebookConnected: boolean = false;
  facebookService: any = null;
  isGoogleAdsConnected: boolean = false;
  googleAdsService: any = null;
  googleAdsCustomers: GoogleAdsCustomer[] = [];
  selectedGoogleAdsCustomer: GoogleAdsCustomer | null = null;
  showGoogleAdsCustomerDropdown: boolean = false;
  googleAdsDropdownOpen: boolean = false;
  private documentClickListener!: (event: Event) => void;
  
  // Plan properties
  planData: any = null;
  planLoading: boolean = false;
  planRevenue: number = 0;
  planTotalBudget: number = 0;
  planMetaBudget: number = 0;
  planGoogleBudget: number = 0;
  currentMonthDate: string = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private brnService: BrnService,
    private fb: FacebookService
  ) {

    // Setup debounced save for Shopify service
    this.shopifySubject.pipe(
      debounceTime(1200), 
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.saveShopifyService();
    });

    // Setup debounced save for Plan data
    this.planSubject.pipe(
      debounceTime(700), 
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.savePlan();
    });
  }

  async ngOnInit() {
    this.botId = this.route.snapshot.paramMap.get('bot_id') || '';
    
    // Initialize current month date (first day of current month)
    this.currentMonthDate = this.getFirstDayOfCurrentMonth();
    
    // Initialize Facebook SDK asynchronously
    this.initializeFacebookSDK();
    
    // Add click listener to close dropdown when clicking outside
    this.documentClickListener = (event) => {
      if (!event.target || !(event.target as Element).closest('.custom-dropdown')) {
        this.closeDropdown();
        this.closeGoogleAdsDropdown();
      }
    };
    document.addEventListener('click', this.documentClickListener);
    
    await Promise.all([
      this.getBotData(),
      this.checkShopifyConnection(),
      this.checkAndCreateTool(),
      this.checkFacebookService(),
      this.checkGoogleAdsService(),
      this.loadPlan()
    ]);
    
    // Sync tool platform with Shopify connection status
    await this.syncToolPlatform();
  }

  private initializeFacebookSDK() {
    // Wait for Facebook SDK to load
    if (typeof window !== 'undefined') {
      const checkFB = () => {
        if ((window as any).FB) {
          console.log('Facebook SDK loaded, initializing...');
          console.log('FACEBOOK_APP_ID:', FACEBOOK_APP_ID);
          
          // Initialize FB directly
          (window as any).FB.init({
            appId: FACEBOOK_APP_ID,
            cookie: true,
            xfbml: true,
            version: 'v20.0'
          });
          
          console.log('FB.init() called with app ID:', FACEBOOK_APP_ID);
          
          // Check if initialization was successful
          setTimeout(() => {
            (window as any).FB.getLoginStatus((response: any) => {
              console.log('FB initialization check - login status:', response);
              if (response.status === 'unknown') {
                console.log('FB initialized but user not logged in - this is normal');
              }
              this.facebookInitialized = true;
              console.log('Facebook SDK initialized successfully');
            });
          }, 1000);
        } else {
          setTimeout(checkFB, 100);
        }
      };
      checkFB();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    
    // Clean up event listener
    if (this.documentClickListener) {
      document.removeEventListener('click', this.documentClickListener);
    }
  }


  onShopifyShopChange() {
    this.shopifySubject.next();
  }

  onShopifyTokenChange() {
    this.shopifySubject.next();
  }



  async getBotData() {
    try {
      this.loading = true;
      const response = await firstValueFrom(this.brnService.getBot(this.botId));
      if (response && response.bot_id) {
        this.botData = response;
      } else {
        this.botData = null;
      }
    } catch (error: any) {
      console.error('Error fetching bot data:', error);
      this.botData = null;
    } finally {
      this.loading = false;
    }
  }

  async checkShopifyConnection() {
    try {
      this.loading = true;
      const response = await firstValueFrom(this.brnService.getService(this.botId));
      
      if (response.status && response.service) {
        const wasConnected = this.isShopifyConnected;
        this.isShopifyConnected = true;
        this.shopifyService = response.service;
        
        // Populate the form fields with existing data
        if (response.service.data?.shop) {
          this.shopifyShop = response.service.data.shop;
        }
        if (response.service.data?.access_token) {
          this.shopifyApiToken = response.service.data.access_token;
        }
        
        // If Shopify just got connected, update tool platform
        if (!wasConnected && this.isToolCreated) {
          await this.updateToolPlatform('shopify');
        }
      } else {
        this.isShopifyConnected = false;
        this.shopifyService = null;
        this.shopifyShop = '';
        this.shopifyApiToken = '';
      }
    } catch (error: any) {
      // If service doesn't exist (404), it means Shopify is not connected
      this.isShopifyConnected = false;
      this.shopifyService = null;
      this.shopifyShop = '';
      this.shopifyApiToken = '';
    } finally {
      this.loading = false;
    }
  }

  connectShopify() {
    if (this.isShopifyConnected) {
      this.disconnectShopify();
    } else {
      this.showShopifyPopup = true;
    }
  }

  async connectSlack() {
    if (this.isSlackConnected) {
      this.disconnectSlack();
    } else {
      try {
        this.slackLoading = true;
        
        // First, create the Slack service
        const serviceResponse = await firstValueFrom(this.brnService.createSlackService(this.botId));
        
        if (serviceResponse.status) {
          console.log('Slack service created successfully:', serviceResponse.service);
          this.redirectToSlackOAuth();
        } else {
          console.error('Failed to create Slack service');
          alert('Failed to create Slack service. Please try again.');
        }
      } catch (error: any) {
        console.error('Error creating Slack service:', error);
        
        // Check if the error is about service already existing
        if (error.error?.error === "This service already exists") {
          console.log('Service already exists, proceeding to OAuth');
          this.redirectToSlackOAuth();
        } else {
          alert('Failed to create Slack service. Please try again.');
        }
      } finally {
        this.slackLoading = false;
      }
    }
  }

  private redirectToSlackOAuth() {
    // Build the Slack OAuth authorization URL
    const authUrl = `https://slack.com/oauth/v2/authorize?` +
      `client_id=${SLACK_CLIENT_ID}&` +
      `scope=${SLACK_SCOPE}&` +
      `user_scope=&` +
      `redirect_uri=${encodeURIComponent(SLACK_REDIRECT_URI)}&` +
      `state=${this.botId}`; // Include bot_id in state for later reference

    // Redirect to Slack OAuth
    window.location.href = authUrl;
  }

  async disconnectShopify() {
    if (confirm('Are you sure you want to disconnect your Shopify store?')) {
      try {
        this.loading = true;
        const response = await firstValueFrom(this.brnService.deleteService(this.botId));
        
        if (response.status) {
          this.isShopifyConnected = false;
          this.shopifyService = null;
          this.shopifyShop = '';
          this.shopifyApiToken = '';
          
          // Update tool to clear ecommerce platform
          await this.updateToolPlatform(null);
        } else {
          alert('Failed to disconnect Shopify store. Please try again.');
        }
      } catch (error: any) {
        console.error('Error disconnecting Shopify:', error);
        alert('Failed to disconnect Shopify store. Please try again.');
      } finally {
        this.loading = false;
      }
    }
  }

  async disconnectSlack() {
    if (confirm('Are you sure you want to disconnect your Slack workspace?')) {
      try {
        this.slackLoading = true;
        const clientId = this.toolData?.properties?.channels?.slack?.client_id;
        
        if (clientId) {
          const response = await firstValueFrom(this.brnService.deleteClient(clientId));
          
          if (response.status) {
            this.isSlackConnected = false;
            this.slackChannel = '';
            
            // Update tool to clear client_id
            await this.updateToolClientId(null);
          } else {
            alert('Failed to disconnect Slack workspace. Please try again.');
          }
        } else {
          alert('No Slack workspace to disconnect.');
        }
      } catch (error: any) {
        console.error('Error disconnecting Slack:', error);
        alert('Failed to disconnect Slack workspace. Please try again.');
      } finally {
        this.slackLoading = false;
      }
    }
  }

  connectFacebook() {
    if (this.isFacebookConnected) {
      this.disconnectFacebook();
    } else {
      console.log('Connect Facebook clicked, initialized:', this.facebookInitialized);
      
      if (!this.facebookInitialized) {
        console.log('Facebook not initialized yet, waiting...');
        // Wait for initialization to complete
        const waitForInit = () => {
          if (this.facebookInitialized) {
            this.performFacebookLogin();
          } else {
            setTimeout(waitForInit, 100);
          }
        };
        waitForInit();
      } else {
        this.performFacebookLogin();
      }
    }
  }

  async disconnectFacebook() {
    if (confirm('Are you sure you want to disconnect your Facebook Ad Account?')) {
      try {
        this.loading = true;
        const response = await firstValueFrom(this.brnService.deleteFacebookService(this.botId));
        
        if (response?.status) {
          this.isFacebookConnected = false;
          this.facebookService = null;
          this.selectedAdAccount = null;
          this.showAdAccountDropdown = false;
          this.adAccounts = [];
          
          // Clear stored access token
          delete (window as any).facebookAccessToken;
          
          console.log('Facebook service disconnected successfully');
        } else {
          alert('Failed to disconnect Facebook Ad Account. Please try again.');
        }
      } catch (error: any) {
        console.error('Error disconnecting Facebook:', error);
        alert('Failed to disconnect Facebook Ad Account. Please try again.');
      } finally {
        this.loading = false;
      }
    }
  }

  private performFacebookLogin() {
    console.log('Performing Facebook login...');
    console.log('FB object available:', typeof (window as any).FB);
    console.log('FB object:', (window as any).FB);
    
    // Use FB directly instead of ngx-facebook service
    if ((window as any).FB) {
      // Check if FB is properly initialized
      (window as any).FB.getLoginStatus((response: any) => {
        console.log('FB Login Status check:', response);
        
        // Now try the login
        (window as any).FB.login((loginResponse: any) => {
          console.log('FB Login response:', loginResponse);
          if (loginResponse.authResponse) {
            const userToken = loginResponse.authResponse.accessToken;
            
            // Store the access token globally for later use
            (window as any).facebookAccessToken = userToken;
            
            this.getAdAccounts(userToken);
          } else {
            console.log('Facebook login failed');
          }
        }, { scope: FACEBOOK_SCOPE });
      });
    } else {
      console.error('FB object not available');
    }
  }

  getAdAccounts(accessToken: string) {
    console.log('Getting ad accounts with token:', accessToken);
    
    const fetchAdAccounts = (url: string) => {
      (window as any).FB.api(url, 'get', { 
        access_token: accessToken, 
        fields: 'id,name', 
        limit: 50 
      }, (response: any) => {
        console.log('Ad accounts response:', response);
        
        if (response && response.data) {
          // Store or append the ad accounts data
          this.adAccounts = this.adAccounts.length > 0 ? 
            [...this.adAccounts, ...response.data] : 
            response.data;
          
          console.log('Updated ad accounts:', this.adAccounts);
          
          // Check for next page and fetch if it exists
          if (response.paging && response.paging.next) {
            fetchAdAccounts(response.paging.next);
          } else {
            // No more pages, handle the final result
            this.handleAdAccountsResult();
            // Auto-open selector if multiple accounts
            if (this.adAccounts.length > 1) {
              this.showAdAccountDropdown = true;
              this.dropdownOpen = true;
            }
          }
        } else if (response && !response.data) {
          // First response with no data, still handle the result
          this.handleAdAccountsResult();
        } else {
          console.error('Failed to fetch ad accounts:', response);
        }
      });
    };
    
    // Start fetching from the base endpoint
    fetchAdAccounts('/me/adaccounts');
  }

  async handleAdAccountsResult() {
    console.log('Final ad accounts count:', this.adAccounts.length);
    
    if (this.adAccounts.length === 1) {
      console.log('one ad account found');
      this.selectedAdAccount = this.adAccounts[0];
      this.showAdAccountDropdown = false;
      
      // Auto-save the Facebook service with the single ad account
      await this.saveFacebookService();
    } else if (this.adAccounts.length > 1) {
      console.log('Multiple ad accounts found, showing dropdown');
      this.showAdAccountDropdown = true;
      this.selectedAdAccount = null; // User needs to select one
    } else {
      console.log('No ad accounts found');
      this.showAdAccountDropdown = false;
      this.selectedAdAccount = null;
    }
  }

  async selectAdAccount(adAccount: any) {
    this.selectedAdAccount = adAccount;
    this.dropdownOpen = false;
    console.log('Selected ad account:', adAccount);
    
    // Save the Facebook service with the selected ad account
    await this.saveFacebookService();
  }

  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
  }

  closeDropdown() {
    this.dropdownOpen = false;
  }

  openFacebookAccountSelector(event?: Event) {
    if (event) { event.stopPropagation(); }
    // When connected, allow the user to change the ad account by opening selector
    this.showAdAccountDropdown = true;
    this.dropdownOpen = true;
  }

  closePopup() {
    this.showShopifyPopup = false;
    this.shopifyUrl = '';
  }

  onShopifyConnect() {
    if (!this.shopifyUrl.trim()) {
      return;
    }

    // Clean the URL to get just the shop name
    let shopName = this.shopifyUrl.trim();
    
    // Remove protocol if present
    shopName = shopName.replace(/^https?:\/\//, '');
    
    // Remove .myshopify.com if present, we'll add it back
    shopName = shopName.replace(/\.myshopify\.com.*$/, '');
    
    // Ensure it ends with .myshopify.com
    const fullShopUrl = shopName.includes('.myshopify.com') ? shopName : `${shopName}.myshopify.com`;

    // Build the OAuth authorization URL
    const authUrl = `https://${fullShopUrl}/admin/oauth/authorize?` +
      `client_id=${SHOPIFY_CLIENT_ID}&` +
      `scope=${SHOPIFY_SCOPE}&` +
      `redirect_uri=${encodeURIComponent(REDIRECT_URI)}&` +
      `state=${this.botId}`; // Include bot_id in state for later reference

    // Redirect to Shopify OAuth
    window.location.href = authUrl;
  }

  onKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.onShopifyConnect();
    }
  }

  goBack() {
    this.router.navigate(['/stores']);
  }

  async checkAndCreateTool() {
    try {
      this.loading = true;
      
      // First, try to get the existing tool
      const response = await firstValueFrom(this.brnService.getTool(this.botId));
      
      if (response.status && response.tool) {
        this.isToolCreated = true;
        this.toolData = response.tool;
        console.log('Tool already exists:', response.tool);
        await this.checkSlackConnection();
      } else {
        await this.createTool();
      }
    } catch (error: any) {
      // If tool doesn't exist (404), create it
      console.log('Tool does not exist, creating...');
      await this.createTool();
    } finally {
      this.loading = false;
    }
  }

  async createTool() {
    try {
      const createResponse = await firstValueFrom(this.brnService.createTool(this.botId));
      
      if (createResponse.status) {
        console.log('Tool created successfully');
        
        // After creating, get the tool data
        const getResponse = await firstValueFrom(this.brnService.getTool(this.botId));
        if (getResponse.status && getResponse.tool) {
          this.isToolCreated = true;
          this.toolData = getResponse.tool;
          console.log('Tool data retrieved:', getResponse.tool);
          await this.checkSlackConnection();
        }
      } else {
        console.error('Failed to create tool');
      }
    } catch (error: any) {
      console.error('Error creating tool:', error);
    }
  }

  async updateToolPlatform(platform: string | null) {
    try {
      const updateResponse = await firstValueFrom(
        this.brnService.updateTool(this.botId, { ecommerce_platform: platform })
      );
      
      if (updateResponse.status) {
        console.log(`Tool platform updated to: ${platform}`);
        
        // Update local tool data
        if (this.toolData) {
          this.toolData.ecommerce_platform = platform;
        }
        
        // Check Slack connection status after update
        await this.checkSlackConnection();
      } else {
        console.error('Failed to update tool platform');
      }
    } catch (error: any) {
      console.error('Error updating tool platform:', error);
    }
  }

  async updateToolClientId(clientId: string | null) {
    try {
      const updateResponse = await firstValueFrom(
        this.brnService.updateTool(this.botId, { 
          channels: { 
            slack: { 
              client_id: clientId 
            } 
          } 
        })
      );
      
      if (updateResponse.status) {
        console.log(`Tool client_id updated to: ${clientId}`);
        
        // Update local tool data
        if (this.toolData && this.toolData.properties) {
          if (!this.toolData.properties.channels) {
            this.toolData.properties.channels = {};
          }
          if (!this.toolData.properties.channels.slack) {
            this.toolData.properties.channels.slack = {};
          }
          this.toolData.properties.channels.slack.client_id = clientId;
        }
        
        // Check Slack connection status after update
        await this.checkSlackConnection();
      } else {
        console.error('Failed to update tool client_id');
      }
    } catch (error: any) {
      console.error('Error updating tool client_id:', error);
    }
  }

  async checkSlackConnection() {
    this.isSlackConnected = !!(this.toolData?.properties?.channels?.slack?.client_id);
    
    // If client_id exists, get client details
    if (this.toolData?.properties?.channels?.slack?.client_id) {
      await this.getSlackClient(this.toolData.properties.channels.slack.client_id);
    }
  }

  private async getSlackClient(clientId: string): Promise<void> {
    try {
      const response = await firstValueFrom(this.brnService.getClient(clientId));
      if (response.client_id) {
        this.slackChannel = response.name;
      }
    } catch (error: any) {
      console.error('Error getting client details:', error);
    }
  }

  async syncToolPlatform() {
    if (!this.isToolCreated) {
      return;
    }
    
    const expectedPlatform = this.isShopifyConnected ? 'shopify' : null;
    const currentPlatform = this.toolData?.ecommerce_platform;
    
    // Update if platforms don't match
    if (currentPlatform !== expectedPlatform) {
      await this.updateToolPlatform(expectedPlatform);
    }
  }


  async checkFacebookService() {
    try {
      const response = await firstValueFrom(this.brnService.getFacebookService(this.botId));
      if (response.status && response.service) {
        this.isFacebookConnected = true;
        this.facebookService = response.service;
        
        // Set the selected ad account from saved service
        if (response.service.data?.ad_account_id) {
          this.selectedAdAccount = {
            id: response.service.data.ad_account_id,
            name: response.service.data.ad_account_name || 'Connected Ad Account'
          };
        }
        
        console.log('Facebook service found:', response.service);
      } else {
        this.isFacebookConnected = false;
        this.facebookService = null;
      }
    } catch (error: any) {
      // Service doesn't exist yet, that's okay
      this.isFacebookConnected = false;
      this.facebookService = null;
      console.log('Facebook service not configured yet');
    }
  }

  async saveFacebookService() {
    if (!this.selectedAdAccount?.id) {
      console.log('No ad account selected, cannot save Facebook service');
      return;
    }

    // We need to get the current access token from the Facebook login
    // For now, we'll need to store it when user logs in
    const accessToken = (window as any).facebookAccessToken; // We'll set this during login
    
    if (!accessToken) {
      console.error('No Facebook access token available');
      return;
    }

    try {
      let response;
      
      if (this.isFacebookConnected) {
        // Update existing service
        response = await firstValueFrom(
          this.brnService.updateFacebookService(this.botId, accessToken, this.selectedAdAccount.id, this.selectedAdAccount.name)
        );
      } else {
        // Create new service
        response = await firstValueFrom(
          this.brnService.createFacebookService(this.botId, accessToken, this.selectedAdAccount.id, this.selectedAdAccount.name)
        );
      }

      if (response?.status) {
        this.isFacebookConnected = true;
        this.facebookService = response.service;
        console.log('Facebook service saved successfully');

        // After Meta Ads connection, create import task for last 30 days ending yesterday
        try {
          const formatDate = (d: Date) => d.toISOString().slice(0, 10);
          const untilDate = new Date();
          untilDate.setDate(untilDate.getDate() - 1); // yesterday
          const sinceDate = new Date(untilDate);
          sinceDate.setDate(sinceDate.getDate() - 30); // 30 days before yesterday
          const since = formatDate(sinceDate);
          const until = formatDate(untilDate);

          await firstValueFrom(
            this.brnService.createImportTask(this.botId, 'meta-ads', since, until)
          );
          console.log('Meta Ads import task created', { since, until });
        } catch (e) {
          console.error('Failed to create Meta Ads import task', e);
        }

        // Ensure daily meta-ads-performance report is scheduled
        try {
          await firstValueFrom(this.brnService.scheduleReportIfMissing(this.botId, 'meta-ads-performance'));
          console.log('Ensured daily meta-ads-performance report is scheduled');
        } catch (e) {
          console.error('Failed to ensure meta-ads-performance report schedule', e);
        }

        // Ensure daily pacing report is scheduled (for Meta Ads)
        try {
          await firstValueFrom(this.brnService.scheduleReportIfMissing(this.botId, 'pacing'));
          console.log('Ensured daily pacing report is scheduled (Meta Ads)');
        } catch (e) {
          console.error('Failed to ensure pacing report schedule (Meta Ads)', e);
        }

        // Reload the page to show the updated connected state
        window.location.reload();
      } else {
        console.error('Failed to save Facebook service');
      }
    } catch (error: any) {
      console.error('Error saving Facebook service:', error);
    }
  }

  async saveShopifyService() {
    if (!this.shopifyShop.trim() || !this.shopifyApiToken.trim()) {
      return;
    }

    try {
      const updateData = {
        data: {
          shop: this.shopifyShop.trim(),
          active: true,
          access_token: this.shopifyApiToken.trim()
        }
      };

      const createData = {
        category: 'ecommerce',
        data: {
          shop: this.shopifyShop.trim(),
          active: true,
          access_token: this.shopifyApiToken.trim()
        }
      };

      let response;
      
      try {
        // First check if service exists
        await firstValueFrom(this.brnService.getService(this.botId));
        // If we get here, service exists, so update it
        response = await firstValueFrom(this.brnService.updateService(this.botId, updateData));
      } catch (error: any) {
        // If 404 error, service doesn't exist so create it
        if (error.status === 404) {
          response = await firstValueFrom(this.brnService.create(this.botId, createData));
        } else {
          throw error; // Re-throw other errors
        }
      }

      if (response?.status) {
        console.log('Shopify service configured successfully');
        this.isShopifyConnected = true;
        this.shopifyService = response.service;
        
        // Update tool platform
        await this.updateToolPlatform('shopify');

        // After Shopify connection, create import task for last 30 days ending yesterday
        try {
          const formatDate = (d: Date) => d.toISOString().slice(0, 10);
          const untilDate = new Date();
          untilDate.setDate(untilDate.getDate() - 1); // yesterday
          const sinceDate = new Date(untilDate);
          sinceDate.setDate(sinceDate.getDate() - 30); // 30 days before yesterday
          const since = formatDate(sinceDate);
          const until = formatDate(untilDate);

          await firstValueFrom(
            this.brnService.createImportTask(this.botId, 'shopify', since, until)
          );
          console.log('Shopify import task created', { since, until });
        } catch (e) {
          console.error('Failed to create Shopify import task', e);
        }

        // Ensure daily store-health report is scheduled
        try {
          await firstValueFrom(this.brnService.scheduleReportIfMissing(this.botId, 'store-health'));
          console.log('Ensured daily store-health report is scheduled');
        } catch (e) {
          console.error('Failed to ensure store-health report schedule', e);
        }

        // Close Shopify modal after successful connection
        this.showShopifyPopup = false;
        // Optionally clear the old url field
        this.shopifyUrl = '';
      } else {
        alert('Failed to save Shopify service. Please try again.');
      }
    } catch (error: any) {
      console.error('Error saving Shopify service:', error);
      alert('Failed to save Shopify service. Please try again.');
    }
  }

  async checkGoogleAdsService() {
    try {
      const response = await firstValueFrom(this.brnService.getGoogleAdsService(this.botId));
      
      if (response?.status && response.service) {
        this.googleAdsService = response.service;
        
        // Check if ad_account_id exists to determine connection status
        if (response.service.data?.ad_account_id) {
          this.isGoogleAdsConnected = true;
          this.selectedGoogleAdsCustomer = {
            ad_account_id: response.service.data.ad_account_id,
            descriptiveName: response.service.data.ad_account_name || `Account ID: ${response.service.data.ad_account_id}`,
            ad_manager_account_id: null,
            manager: false,
            level: "0",
            status: "ENABLED"
          };
          this.showGoogleAdsCustomerDropdown = false;
        } else {
          // Service exists but no customer selected - fetch customers for dropdown
          this.isGoogleAdsConnected = false;
          this.showGoogleAdsCustomerDropdown = true;
          await this.getGoogleAdsCustomers();
        }
        
        console.log('Google Ads service found:', response.service);
      } else {
        this.isGoogleAdsConnected = false;
        this.googleAdsService = null;
        this.showGoogleAdsCustomerDropdown = false;
      }
    } catch (error: any) {
      console.log('No Google Ads service found');
      this.isGoogleAdsConnected = false;
      this.googleAdsService = null;
      this.showGoogleAdsCustomerDropdown = false;
    }
  }

  async connectGoogleAds() {
    if (this.isGoogleAdsConnected) {
      this.disconnectGoogleAds();
    } else {
      try {
        console.log('Initiating Google Ads OAuth...');
        
        // Call the init auth endpoint with our redirect URL
        const response = await firstValueFrom(
          this.brnService.initGoogleAdsAuth(GOOGLE_ADS_REDIRECT_URI)
        );

        if (response?.status && response.url) {
          // Add bot_id as state parameter to the OAuth URL
          const url = new URL(response.url);
          url.searchParams.set('state', this.botId);
          
          // Redirect to Google's OAuth page
          window.location.href = url.toString();
        } else {
          alert('Failed to initialize Google Ads connection. Please try again.');
        }
      } catch (error: any) {
        console.error('Error initiating Google Ads OAuth:', error);
        alert('Failed to initialize Google Ads connection. Please try again.');
      }
    }
  }

  async disconnectGoogleAds() {
    if (confirm('Are you sure you want to disconnect your Google Ads account?')) {
      try {
        this.loading = true;
        const response = await firstValueFrom(this.brnService.deleteGoogleAdsService(this.botId));
        
        if (response?.status) {
          this.isGoogleAdsConnected = false;
          this.googleAdsService = null;
          this.googleAdsCustomers = [];
          this.selectedGoogleAdsCustomer = null;
          this.showGoogleAdsCustomerDropdown = false;
          this.googleAdsDropdownOpen = false;
          
          console.log('Google Ads service disconnected successfully');
        } else {
          alert('Failed to disconnect Google Ads account. Please try again.');
        }
      } catch (error: any) {
        console.error('Error disconnecting Google Ads:', error);
        alert('Failed to disconnect Google Ads account. Please try again.');
      } finally {
        this.loading = false;
      }
    }
  }

  async getGoogleAdsCustomers() {
    try {
      const response = await firstValueFrom(this.brnService.getGoogleAdsCustomers(this.botId));
      
      if (response?.status && response.customers) {
        this.googleAdsCustomers = response.customers;
        console.log('Google Ads customers fetched:', response.customers);
      } else {
        console.log('No Google Ads customers found');
        this.googleAdsCustomers = [];
      }
    } catch (error: any) {
      console.error('Error fetching Google Ads customers:', error);
      this.googleAdsCustomers = [];
    }
  }

  async selectGoogleAdsCustomer(customer: GoogleAdsCustomer) {
    this.selectedGoogleAdsCustomer = customer;
    this.googleAdsDropdownOpen = false;
    console.log('Selected Google Ads customer:', customer);
    
    // Update the Google Ads service with the selected customer_id
    await this.saveGoogleAdsCustomer();
  }

  toggleGoogleAdsDropdown() {
    this.googleAdsDropdownOpen = !this.googleAdsDropdownOpen;
  }

  closeGoogleAdsDropdown() {
    this.googleAdsDropdownOpen = false;
  }

  async saveGoogleAdsCustomer() {
    if (!this.selectedGoogleAdsCustomer?.ad_account_id) {
      console.log('No customer selected, cannot save Google Ads service');
      return;
    }

    try {
      const response = await firstValueFrom(
        this.brnService.updateGoogleAdsService(this.botId, this.selectedGoogleAdsCustomer.ad_account_id)
      );

      if (response?.status) {
        this.isGoogleAdsConnected = true;
        this.googleAdsService = response.service;
        this.showGoogleAdsCustomerDropdown = false;
        console.log('Google Ads customer saved successfully');

        // After Google Ads customer selection, create import task for last 30 days ending yesterday
        try {
          const formatDate = (d: Date) => d.toISOString().slice(0, 10);
          const untilDate = new Date();
          untilDate.setDate(untilDate.getDate() - 1); // yesterday
          const sinceDate = new Date(untilDate);
          sinceDate.setDate(sinceDate.getDate() - 30); // 30 days before yesterday
          const since = formatDate(sinceDate);
          const until = formatDate(untilDate);

          await firstValueFrom(
            this.brnService.createImportTask(this.botId, 'google-ads', since, until)
          );
          console.log('Google Ads import task created', { since, until });
        } catch (e) {
          console.error('Failed to create Google Ads import task', e);
        }

        // Ensure daily google-ads-performance report is scheduled
        try {
          await firstValueFrom(this.brnService.scheduleReportIfMissing(this.botId, 'google-ads-performance'));
          console.log('Ensured daily google-ads-performance report is scheduled');
        } catch (e) {
          console.error('Failed to ensure google-ads-performance report schedule', e);
        }

        // Ensure daily pacing report is scheduled (for Google Ads)
        try {
          await firstValueFrom(this.brnService.scheduleReportIfMissing(this.botId, 'pacing'));
          console.log('Ensured daily pacing report is scheduled (Google Ads)');
        } catch (e) {
          console.error('Failed to ensure pacing report schedule (Google Ads)', e);
        }
        
        // Reload the page to show the updated connected state
        window.location.reload();
      } else {
        console.error('Failed to save Google Ads customer');
      }
    } catch (error: any) {
      console.error('Error saving Google Ads customer:', error);
    }
  }

  // Plan related methods
  getFirstDayOfCurrentMonth(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // months are 0-indexed
    return `${year}-${month}-01`;
  }

  async loadPlan() {
    try {
      this.planLoading = true;
      const response = await firstValueFrom(this.brnService.getPlan(this.botId, this.currentMonthDate));
      
      if (response.status) {
        if (response.plan) {
          // Plan exists, populate with actual data
          this.planData = response.plan;
          this.planRevenue = response.plan.revenue?.total || 0;
          this.planTotalBudget = response.plan.budget?.total || 0;
          this.planMetaBudget = response.plan.budget?.channels?.['meta-ads'] || 0;
          this.planGoogleBudget = response.plan.budget?.channels?.['google-ads'] || 0;
          console.log('Plan loaded successfully:', response.plan);
        } else {
          // Plan is null (no plan exists yet), but API call was successful
          // Create a placeholder plan object to enable editing
          this.planData = {
            id: '',
            bot_id: this.botId,
            date: this.currentMonthDate,
            budget: { total: 0, channels: { 'meta-ads': 0, 'google-ads': 0 } },
            revenue: { total: 0 }
          };
          this.planRevenue = 0;
          this.planTotalBudget = 0;
          this.planMetaBudget = 0;
          this.planGoogleBudget = 0;
          console.log('No plan exists yet, created placeholder for editing');
        }
      } else {
        // API call failed
        this.planData = null;
        this.planRevenue = 0;
        this.planTotalBudget = 0;
        this.planMetaBudget = 0;
        this.planGoogleBudget = 0;
      }
    } catch (error: any) {
      // API call failed (404 or other error)
      console.log('No plan found for current month, using defaults');
      this.planData = null;
      this.planRevenue = 0;
      this.planTotalBudget = 0;
      this.planMetaBudget = 0;
      this.planGoogleBudget = 0;
    } finally {
      this.planLoading = false;
    }
  }

  onPlanValueChange() {
    // Create a plan data object to enable editing UI if it doesn't exist
    if (!this.planData) {
      this.planData = {
        id: '',
        bot_id: this.botId,
        date: this.currentMonthDate,
        budget: { total: 0, channels: { 'meta-ads': 0, 'google-ads': 0 } },
        revenue: { total: 0 }
      };
    }
    
    // Trigger debounced save
    this.planSubject.next();
  }

  async savePlan() {
    try {
      const planRequest: BrnPlanRequest = {
        budget: {
          total: this.planTotalBudget || 0,
          channels: {
            'meta-ads': this.planMetaBudget || 0,
            'google-ads': this.planGoogleBudget || 0
          }
        },
        revenue: {
          total: this.planRevenue || 0
        }
      };

      const response = await firstValueFrom(
        this.brnService.setPlan(this.botId, this.currentMonthDate, planRequest)
      );

      if (response.status) {
        //this.planData = response.plan;
        console.log('Plan saved successfully:', response.plan);
      } else {
        console.error('Failed to save plan');
      }
    } catch (error: any) {
      console.error('Error saving plan:', error);
    }
  }

  formatNumber(event: any, field: string) {
    const input = event.target;
    let value = input.value.replace(/[^\d]/g, ''); // Remove non-digits
    
    if (value === '') {
      value = '0';
    }
    
    // Convert to number and format with commas
    const numValue = parseInt(value, 10);
    const formattedValue = numValue.toLocaleString();
    
    // Update the input value with formatted number
    input.value = formattedValue;
    
    // Update the corresponding model property with the numeric value
    switch (field) {
      case 'revenue':
        this.planRevenue = numValue;
        break;
      case 'total':
        this.planTotalBudget = numValue;
        break;
      case 'meta':
        this.planMetaBudget = numValue;
        break;
      case 'google':
        this.planGoogleBudget = numValue;
        break;
    }
    
    // Trigger the change event to update the model
    this.onPlanValueChange();
  }

  // Method to handle input changes and update the model
  onInputChange(event: any, field: string) {
    const input = event.target;
    let value = input.value.replace(/[^\d]/g, ''); // Remove non-digits
    
    if (value === '') {
      value = '0';
    }
    
    const numValue = parseInt(value, 10);
    
    // Update the corresponding model property
    switch (field) {
      case 'revenue':
        this.planRevenue = numValue;
        break;
      case 'total':
        this.planTotalBudget = numValue;
        break;
      case 'meta':
        this.planMetaBudget = numValue;
        break;
      case 'google':
        this.planGoogleBudget = numValue;
        break;
    }
    
    // Trigger the change event to update the model
    this.onPlanValueChange();
  }

  // Helper method to format a number with commas
  formatNumberWithCommas(value: number): string {
    return value.toLocaleString();
  }

  // Navigation change handler for settings nav
  onNavigationChange(item: string): void {
    console.log('Navigation changed to:', item);
    // TODO: Add navigation logic here when other pages are implemented
    // For now, we're staying on the integrations page
  }
}
